// 任务队列：按状态分组（卡住置顶）、组内按优先级 → 更新时间排序、筛选
import { PRIORITY_RANK, type TaskDTO, type TaskStatus } from "@csp/shared";

export interface QueueGroup {
  id: string;
  title: string;
  statuses: TaskStatus[];
  /** 需要人处理的组高亮 */
  attention: boolean;
}

export const GROUPS: QueueGroup[] = [
  { id: "blocked", title: "卡住了，需要处理", statuses: ["blocked"], attention: true },
  { id: "scan", title: "等扫码", statuses: ["waiting_scan"], attention: true },
  { id: "review", title: "等审核", statuses: ["review_required"], attention: true },
  { id: "running", title: "执行中", statuses: ["running"], attention: false },
  { id: "queued", title: "排队中", statuses: ["queued"], attention: false },
  { id: "draft", title: "草稿 / 就绪（还没开始）", statuses: ["draft", "ready"], attention: false },
  { id: "closed", title: "已结束", statuses: ["done", "failed", "cancelled", "archived"], attention: false },
];

export type QueueFilter = "all" | "attention" | "active" | "draft" | "closed";

export const FILTERS: { id: QueueFilter; label: string }[] = [
  { id: "all", label: "全部" },
  { id: "attention", label: "需要我处理" },
  { id: "active", label: "进行中" },
  { id: "draft", label: "草稿/就绪" },
  { id: "closed", label: "已结束" },
];

const FILTER_GROUPS: Record<QueueFilter, string[]> = {
  all: GROUPS.map((g) => g.id),
  attention: ["blocked", "scan", "review"],
  active: ["blocked", "scan", "review", "running", "queued"],
  draft: ["draft"],
  closed: ["closed"],
};

export function sortTasks(list: TaskDTO[]): TaskDTO[] {
  return [...list].sort((a, b) => {
    const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    if (p !== 0) return p;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

/** 分组：只返回非空组，顺序固定（卡住永远第一） */
export function groupTasks(list: TaskDTO[], filter: QueueFilter = "all"): { group: QueueGroup; tasks: TaskDTO[] }[] {
  const allowed = new Set(FILTER_GROUPS[filter]);
  const out: { group: QueueGroup; tasks: TaskDTO[] }[] = [];
  for (const g of GROUPS) {
    if (!allowed.has(g.id)) continue;
    const tasks = sortTasks(list.filter((t) => g.statuses.includes(t.status)));
    if (tasks.length) out.push({ group: g, tasks });
  }
  return out;
}

/** 列表里替换 / 插入单个任务（实时更新） */
export function upsertTask(list: TaskDTO[], t: TaskDTO): TaskDTO[] {
  const i = list.findIndex((x) => x.id === t.id);
  if (i < 0) return [t, ...list];
  const next = [...list];
  next[i] = t;
  return next;
}

export function countAttention(list: TaskDTO[]): number {
  return list.filter((t) => ["blocked", "waiting_scan", "review_required"].includes(t.status)).length;
}

/** 需要人处理的状态（「需要优先处理」列表 + 排序置顶），顺序即优先级 */
export const ATTENTION_STATUSES: TaskStatus[] = ["blocked", "waiting_scan", "review_required"];

/** 任务表默认排序：需要处理的置顶（卡住 → 等扫码 → 等审核），其余按更新时间从新到旧 */
export function sortQueue(list: TaskDTO[]): TaskDTO[] {
  const rank = (s: TaskStatus) => {
    const i = ATTENTION_STATUSES.indexOf(s);
    return i < 0 ? ATTENTION_STATUSES.length : i;
  };
  return [...list].sort((a, b) => {
    const r = rank(a.status) - rank(b.status);
    if (r !== 0) return r;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

/** 状态多选筛选：同字段 OR；什么都不选 = 全部 */
export function filterByStatus(list: TaskDTO[], statuses: TaskStatus[]): TaskDTO[] {
  if (!statuses.length) return list;
  const set = new Set(statuses);
  return list.filter((t) => set.has(t.status));
}

export function countByStatus(list: TaskDTO[]): Partial<Record<TaskStatus, number>> {
  const out: Partial<Record<TaskStatus, number>> = {};
  for (const t of list) out[t.status] = (out[t.status] ?? 0) + 1;
  return out;
}
